import { useEffect, useState } from "react";
import { Button, Card, Row, Col, Spinner, Alert, Table } from "react-bootstrap"
import { Form } from "react-bootstrap"
import { useNavigate } from "react-router-dom";
import { FileDown, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";
import { fetchPorts, fetchPreventiveMaintenanceReport } from "./services/api";
import { Port } from "./types";

export default function PreventiveMaintenanceReport() {
    const navigate = useNavigate();
    const [startDate, setStartDate] = useState<string>(new Date().toISOString().slice(0, 10));
    const [endDate, setEndDate] = useState<string>(new Date().toISOString().slice(0, 10));
    const [portId, setPortId] = useState<string>('');
    const [portOptions, setPortOptions] = useState<{ id: string; name: string }[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<any[]>([]);

    useEffect(() => {
        fetchPorts()
            .then(response => {
                const ports = Array.isArray(response.data.data) ? response.data.data : [];
                setPortOptions(ports.map((port: Port) => ({
                    id: String(port.id),
                    name: port.name
                })));
            })
            .catch(error => {
                console.error('Error fetching ports:', error);
                setPortOptions([]);
                setError('Failed to fetch ports');
            });
    }, []);

    function handleFetchClick(): void {
        setLoading(true);
        setError(null);
        setData([]);
        fetchPreventiveMaintenanceReport(startDate, endDate, portId)
            .then(response => {
                setData(response);
            })
            .catch(error => {
                console.error('Error fetching preventive maintenance report:', error);
                setError('Failed to fetch preventive maintenance report');
            })
            .finally(() => {
                setLoading(false);
            });
    }

    function handleExportToPdf(): void {
        console.log('Exporting to PDF...');
    }

    function handleExportToExcel(): void {
        if (data.length === 0) {
            setError('No data to export. Please fetch data first.');
            return;
        }
        setError(null);
        const exportData = data.map((item) => ({
            'Site Name': item.site_name ?? '',
            'Employee Name': item.employee_name ?? '',
            'Maintenance Date': item.maintenance_date ?? '',
            'Status': item.status ?? '',
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        worksheet['!cols'] = [
            { wch: 20 },
            { wch: 25 },
            { wch: 18 },
            { wch: 12 },
        ];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Preventive Maintenance');
        const filename = `Preventive_Maintenance_Report_${startDate}_to_${endDate}.xlsx`;
        XLSX.writeFile(workbook, filename);
    }

    return (
        <div className="p-4 p-md-5" style={{ height: '100%', overflowY: 'auto' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>Preventive Maintenance Report</h2>
                <Button variant="outline-primary" onClick={() => navigate('/')}>Go to Home</Button>
            </div>
            <Card className="mb-3 shadow-sm">
                <Card.Body>
                    <Form>
                        <Row className="align-items-end g-2">
                            <Col md>
                                <Form.Group controlId="startDate">
                                    <Form.Label>Start Date</Form.Label>
                                    <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                                </Form.Group>
                            </Col>
                            <Col md>
                                <Form.Group controlId="endDate">
                                    <Form.Label>End Date</Form.Label>
                                    <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                                </Form.Group>
                            </Col>
                            <Col md>
                                <Form.Group controlId="port">
                                    <Form.Label>Location</Form.Label>
                                    <Form.Select value={portId} onChange={e => setPortId(e.target.value)}>
                                        <option value="">All</option>
                                        {portOptions.map(port => (
                                            <option key={port.id} value={port.name}>{port.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>


                            <Col md="auto">
                                <Button variant="primary" onClick={handleFetchClick}>
                                    Fetch Data
                                </Button>
                            </Col>
                            <Col md="auto" className="d-flex gap-2">
                                <Button
                                    variant="primary"
                                    onClick={handleExportToPdf}
                                    title="Export to PDF"
                                    disabled={data.length === 0}
                                >
                                    <FileDown size={18} />
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleExportToExcel}
                                    title="Export to Excel"
                                    disabled={data.length === 0}
                                >
                                    <FileSpreadsheet size={18} />
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {loading && <Spinner animation="border" />}
            {error && <Alert variant="danger">{error}</Alert>}
            {data.length > 0 && (
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Site Name</th>
                            <th>Employee Name</th>
                            <th>Maintenance Date</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item, index) => (
                            <tr key={index} onClick={() => { navigate(`/maintenance-details`, { state: { data: item } }); }}>
                                <td>{item.site_name}</td>
                                <td>{item.employee_name}</td>
                                <td>{item.maintenance_date}</td>
                                <td>{item.status}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

        </div>

    );
}